import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { TimeLog, TodayResponse, TeamMember, BreakType } from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useTodayLog() {
  return useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: async () => {
      const { data } = await api.get<TodayResponse>('/api/time-logs/today');
      return data;
    },
  });
}

export function useMyLogs(limit = 20) {
  return useQuery({
    queryKey: ['attendance', 'logs', limit],
    queryFn: async () => {
      const { data } = await api.get<{ logs: TimeLog[] }>(`/api/time-logs?limit=${limit}`);
      return data.logs ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

/** Team roster (today) — admin/superadmin only. */
export function useTeamAttendance(enabled: boolean) {
  return useQuery({
    queryKey: ['attendance', 'team'],
    queryFn: async () => {
      const { data } = await api.get<{ team: TeamMember[] }>('/api/time-logs/team');
      return data.team ?? [];
    },
    enabled,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useAttendanceMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['attendance'] });
  };

  const checkIn = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; time: string }>('/api/time-logs/checkin');
      return data;
    },
    onSuccess: invalidate,
  });

  const checkOut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; time: string; hours: number }>('/api/time-logs/checkout');
      return data;
    },
    onSuccess: invalidate,
  });

  const startBreak = useMutation({
    mutationFn: async (breakType: BreakType) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/time-logs/breaks/start', { breakType });
      return data;
    },
    onSuccess: invalidate,
  });

  const endBreak = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; duration: number }>('/api/time-logs/breaks/end');
      return data;
    },
    onSuccess: invalidate,
  });

  return { checkIn, checkOut, startBreak, endBreak };
}
